
import withRouterParams from "../../../withRouterParams";
import { hideLoading, showLoading } from "../../../loading";
import swal from 'sweetalert';
import request from "../../../request";
import CenteredMessage from "../../../components/CenteredMessage";
import EntityInfo from "../EntityInfo";
import LabeledContainer from "../../../components/LabeledContainer";
import PillLabel from "../../../components/PillLabel";
import ResponsiveTable from "../../../components/ResponsiveTable";
import DateTime from "../../../components/DateTime";
import StyledLink from "../../../components/StyledLink";
import links from "../../../links";
import RoundedIconButton from "../../../components/RoundedIconButton";
import { Refresh } from "@mui/icons-material";
import ConnectToQuickbooksButton from "../../../components/ConnectToQuickbooksButton";
import { ConnectEntityToQuickbooks } from "../../../components/ConnectEntityToQuickbooks";
import NavigationStateSaver from "../../../components/NavigationStateSaver";
import { AGENT_TYPE, DOCUMENT_NAME, USER_ROLE } from "../../../../../shared/constants";
import Attachment from "../../../components/Attachment";
import { connect } from "react-redux";
import { formatMoney, hasIntersection } from "../../../../../shared/utils";


function Earnings({ earnings }) {
   return <LabeledContainer label="Earnings" className="rounded-lg p-4">
      <ResponsiveTable
         cols={[
            {
               field: 'amount',
               caption: 'Amount',
               render: amount => formatMoney(amount)
            },
            {
               field: 'for',
               caption: 'For',
               render: (_, earning) => {
                  let caption, to;
                  if (earning.installation) {
                     caption = 'Installation';
                     to = links.installation(earning.installation._id);
                  } else if (earning.loan) {
                     caption = 'Loan';
                     to = links.loan(earning.loan._id);
                  } else {
                     return null;
                  }

                  return to && <StyledLink to={to}>
                     {caption}
                  </StyledLink>
               }
            },
            {
               field: 'collected',
               caption: 'Collected',
               render: collected => collected ? <span className="text-green-500">Yes</span> : <span className="text-red-500">No</span>
            },
            {
               field: 'payment_type',
               caption: 'Payment Type',
               render: payment_type => payment_type ? <PillLabel className="uppercase">{payment_type}</PillLabel> : '–'
            },
            {
               field: 'created_at',
               caption: 'Created At',
               render: created_at => <DateTime value={created_at} />
            }
         ]}
         rows={earnings}
      />
   </LabeledContainer>
}

function Installations({ installations }) {
   return <LabeledContainer label="Installations" className="rounded-lg p-4">
      <ResponsiveTable
         cols={[
            {
               field: 'product',
               caption: 'Product',
               render: product => product ? product.name : '–'
            },
            {
               field: 'customer',
               caption: 'Customer',
               render: customer => customer ? <StyledLink to={links.customer(customer._id)}>
                  {customer.name} {customer.surname}
               </StyledLink> : '–'
            },
            {
               field: 'Loan',
               caption: 'Loan',
               render: loan => loan ? <StyledLink to={links.loan(loan._id)}>{loan.loan_no}</StyledLink> : '–'
            },
            {
               field: 'status',
               caption: 'Status',
               render: status => <PillLabel>{status.toUpperCase()}</PillLabel>
            },
            {
               field: 'created_at',
               caption: 'Created At',
               render: created_at => <DateTime value={created_at} />
            }
         ]}
         rows={installations}
      />
   </LabeledContainer>
}

function Loans({ loans }) {
   return <LabeledContainer label="Loans" className="rounded-lg p-4">
      <ResponsiveTable
         cols={[
            {
               field: 'loan_no',
               caption: 'Loan No',
               render: (loan_no, loan) => <StyledLink to={links.loan(loan._id)}>
                  {loan_no}
               </StyledLink>
            },
            {
               field: 'customer',
               caption: 'Customer',
               render: (customer) => customer ? <StyledLink to={links.customer(customer._id)}>
                  {customer.name} {customer.surname}
               </StyledLink>: '–'
            },
            {
               field: 'product',
               caption: 'Product',
               render: product => product ? product.name : '–'
            },
            {
               field: 'price',
               caption: 'Price',
               render: price => formatMoney(price)
            },
            {
               field: 'status',
               caption: 'Status',
               render: status => <PillLabel>{status.toUpperCase()}</PillLabel>
            },
            {
               field: 'created_at',
               caption: 'Applied',
               render: created_at => <DateTime value={created_at} />
            }
         ]}
         rows={loans}
      />
   </LabeledContainer>
}


class _Agent extends NavigationStateSaver {

   state = {
      agent: null,
      connectToQuickbooksModalOpen: false
   }

   openConnectToQuickbooksModal = () => {
      this.setState({ connectToQuickbooksModalOpen: true });
   }

   closeConnectToQuickbooksModal = agent => {
      this.setState({ connectToQuickbooksModalOpen: false });

      if (agent) {
         this.setState({
            agent: {
               ...this.state.agent,
               ...agent
            }
         })
      }
   }

   getId() {
      return this.props.params.id;
   }

   fetchAgent = async () => {
      try {

         showLoading();
         const res = await request.get(`/api/general/agents/${this.getId()}`);
         this.setState({ agent: res.data });
      } catch (err) {
         swal("Error", err.toString(), "error");
      } finally {
         hideLoading();
      }
   }

   async componentDidMount() {
      await super.componentDidMount();
      if (!this.state.agent)
         this.fetchAgent();
   }

   render() {
      
      const { agent } = this.state;
      let content;

      const updatingRoles = [ USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN, ];

      if (agent) {
         if (agent.type === AGENT_TYPE.SALES)
            updatingRoles.push(USER_ROLE.SALES_MANAGER);
         else if (agent.type === AGENT_TYPE.TECHNICAL)
            updatingRoles.push(USER_ROLE.TECHNICAL_MANAGER);
      }

      const canUpdate = hasIntersection(this.props.roles, updatingRoles);

      if (!agent) {
         content = <CenteredMessage
            message="Data not fetched"
            onRefresh={this.fetchAgent}
         />
      } else {
         content = <div className="p-2 md:p-8 text-gray-600 flex flex-col gap-8">

            <h1 className="text-xl md:text-3xl font-bold flex items-center gap-2 uppercase">
               {agent.name} {agent.surname} <PillLabel>{agent.type.toUpperCase()}</PillLabel>
            </h1>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <EntityInfo info={agent} />
               <LabeledContainer label='Attachments' className='p-4 rounded-lg grid grid-cols-1 md:grid-cols-2 gap-4'>
                  {
                     Object.values(DOCUMENT_NAME.AGENT).map(name => {
                        const attachment = agent.attachments?.find(a => a.name === name);
                        const url = attachment ? attachment.url : null;
                        return <div key={name}>
                           <Attachment
                              name={name}
                              url={url}
                              onSave={
                                 async data => {
                                    const res = await request.post(`/api/general/agents/${agent._id}/attachments`, data);
                                    this.setState({ agent: res.data });
                                 }
                              }
                              canEdit={canUpdate}
                           />
                        </div>
                     })
                  }
               </LabeledContainer>
            </div>

            {agent.installations && <Installations installations={agent.installations} />}
            {agent.loans && <Loans loans={agent.loans} />}

            <Earnings earnings={agent.earnings} />

         </div>
      }

      return <>
         <div className="h-full grid grid-rows-[1fr,auto">
            <div className="overflow-auto">
               {content}
            </div>
            <div className="flex items-center justify-end gap-4 p-2 border-t-gray-200 border-t">
               {
                  agent && !Boolean(agent.quickbooks_id) && <ConnectToQuickbooksButton
                     disabled={!canUpdate}
                     onClick={this.openConnectToQuickbooksModal}
                  />
               }

               <RoundedIconButton onClick={this.fetchAgent} color="secondary">
                  <Refresh />
               </RoundedIconButton>
            </div>
         </div>
         {
            this.state.connectToQuickbooksModalOpen && <ConnectEntityToQuickbooks
               quickbooksIdKey="quickbooks_id"
               submitUrl={"/api/general/quickbooks/vendor-connection"}
               fetchData={async () => {
                  const res = await request.get("/api/general/quickbooks/vendors");
                  return res.data.vendors;
               }}
               entityKey="agent"
               entityId={this.getId()}
               renderOption={(props, option) => {
                  return <li {...props} className="border-b last:border-none cursor-pointer hover:bg-gray-100">
                     <div className='px-4 py-2'>
                        {option.name}
                     </div>
                  </li>
               }}
               onClose={this.closeConnectToQuickbooksModal}
            />
         }
      </>
   }
}

const mapStateToProps = state => ({
   roles: state.user.roles || [],
})

const Agent = connect(mapStateToProps)(withRouterParams(_Agent));

export default Agent;